import React, { useState, useEffect, useRef } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Plus, Trash2, Info, KeyIcon } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { Tooltip } from "../ui/tooltip";
import { WebhookActionConfig } from "../../types/AgentAction";

interface AgentActionWebhookEditProps {
  config: WebhookActionConfig;
  onChange: (config: WebhookActionConfig) => void;
  actionName?: string;
  description?: string;
  onActionNameChange?: (name: string) => void;
  onDescriptionChange?: (description: string) => void;
}

const AgentActionWebhookEdit: React.FC<AgentActionWebhookEditProps> = ({
  config,
  onChange,
  actionName,
  description,
  onActionNameChange,
  onDescriptionChange,
}) => {
  const [formData, setFormData] = useState<WebhookActionConfig>({
    url: "",
    method: "POST",
    headers: { "Content-Type": "application/json" },
    payload_template: "{}",
    timeout_ms: 600000, // 10 minutes default
    auth: {
      type: "none",
      token: "",
      header: "Authorization",
      key: "",
      username: "",
      password: "",
    },
    ...config,
  });

  const [newHeaderKey, setNewHeaderKey] = useState("");
  const [newHeaderValue, setNewHeaderValue] = useState("");
  const [payloadError, setPayloadError] = useState<string | null>(null);
  const previousPayloadRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const newFormData = {
      url: "",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      payload_template: "{}",
      timeout_ms: 600000,
      auth: {
        type: "none",
        token: "",
        header: "Authorization",
        key: "",
        username: "",
        password: "",
      },
      ...config,
    };
    setFormData(newFormData);
    
    // Only validate and clear error if payload template actually changed
    const payloadTemplate = newFormData.payload_template;
    if (payloadTemplate !== previousPayloadRef.current) {
      previousPayloadRef.current = payloadTemplate;
      
      if (payloadTemplate && typeof payloadTemplate === "string" && payloadTemplate.trim()) {
        try {
          JSON.parse(payloadTemplate);
          setPayloadError(null);
        } catch (e) {
          setPayloadError("Invalid JSON format");
        }
      } else {
        setPayloadError(null);
      }
    }
  }, [config]);

  const updateConfig = (updates: Partial<WebhookActionConfig>) => {
    const newConfig = { ...formData, ...updates };
    setFormData(newConfig);
    onChange(newConfig);
  };

  const handleAuthChange = (field: string, value: string) => {
    updateConfig({
      auth: {
        ...formData.auth,
        [field]: value,
      },
    });
  };

  const addHeader = () => {
    if (newHeaderKey && newHeaderValue) {
      updateConfig({
        headers: {
          ...formData.headers,
          [newHeaderKey]: newHeaderValue,
        },
      });
      setNewHeaderKey("");
      setNewHeaderValue("");
    }
  };

  const removeHeader = (key: string) => {
    const newHeaders = { ...formData.headers };
    delete newHeaders[key];
    updateConfig({ headers: newHeaders });
  };

  const validateJSON = (value: string) => {
    try {
      JSON.parse(value);
      setPayloadError(null);
      return true;
    } catch (e) {
      setPayloadError("Invalid JSON format");
      return false;
    }
  };

  const handlePayloadChange = (value: string) => {
    updateConfig({ payload_template: value });
    if (value.trim()) {
      validateJSON(value);
    } else {
      setPayloadError(null);
    }
  };

  const insertWebhookUrlVariable = () => {
    const cursorPosition =
      (document.getElementById("payload-template") as HTMLTextAreaElement)
        ?.selectionStart || 0;
    const before = formData.payload_template.substring(0, cursorPosition);
    const after = formData.payload_template.substring(cursorPosition);
    const newValue = before + "{{webhook_url}}" + after;
    handlePayloadChange(newValue);
  };

  const hasWebhookUrl = formData.payload_template.includes("{{webhook_url}}");

  return (
    <div className="space-y-6">
      {/* Action Name and Description */}
      {onActionNameChange && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="action-name"
              className="text-sm font-gc-med text-foreground">
              Action Name
            </Label>
            <Input
              id="action-name"
              value={actionName || ""}
              onChange={(e) => onActionNameChange(e.target.value)}
              placeholder="e.g., Notify Stripe Payment"
            />
          </div>

          {onDescriptionChange && (
            <div className="space-y-2">
              <Label
                htmlFor="action-description"
                className="text-sm font-gc-med text-foreground">
                Description
              </Label>
              <Textarea
                id="action-description"
                value={description || ""}
                onChange={(e) => onDescriptionChange(e.target.value)}
                placeholder="Describe what this webhook action does"
                rows={2}
              />
            </div>
          )}
        </div>
      )}

      {/* URL Configuration */}
      <div className="space-y-4 p-4 border border-border rounded-lg">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-gc-med text-foreground">
            External Service Configuration
          </h3>
          <Tooltip content="Configure the external service that will receive the webhook URL">
            <Info className="w-4 h-4 text-muted-foreground" />
          </Tooltip>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="webhook-url"
              className="text-sm font-gc-med text-foreground">
              Service URL
            </Label>
            <Input
              id="webhook-url"
              value={formData.url}
              onChange={(e) => updateConfig({ url: e.target.value })}
              placeholder="https://api.example.com/webhook/register"
            />
            <p className="text-xs text-muted-foreground">
              The external service endpoint where the agent will send the
              webhook URL
            </p>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="webhook-method"
              className="text-sm font-gc-med text-foreground">
              HTTP Method
            </Label>
            <Select
              value={formData.method}
              onValueChange={(value) => updateConfig({ method: value })}>
              <SelectTrigger id="webhook-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GET">GET</SelectItem>
                <SelectItem value="POST">POST</SelectItem>
                <SelectItem value="PUT">PUT</SelectItem>
                <SelectItem value="PATCH">PATCH</SelectItem>
                <SelectItem value="DELETE">DELETE</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Headers */}
      <div className="space-y-4 p-4 border border-border rounded-lg">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-gc-med text-foreground">Headers</h3>
          <Tooltip content="HTTP headers to include in the request">
            <Info className="w-4 h-4 text-muted-foreground" />
          </Tooltip>
        </div>

        <div className="space-y-2">
          {Object.entries(formData.headers || {}).map(([key, value]) => (
            <div key={key} className="flex items-center gap-2">
              <Input value={key} disabled className="flex-1 bg-muted" />
              <Input value={value} disabled className="flex-1 bg-muted" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeHeader(key)}
                className="text-destructive hover:text-destructive">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}

          <div className="flex items-center gap-2">
            <Input
              placeholder="Header name"
              value={newHeaderKey}
              onChange={(e) => setNewHeaderKey(e.target.value)}
              className="flex-1"
            />
            <Input
              placeholder="Header value"
              value={newHeaderValue}
              onChange={(e) => setNewHeaderValue(e.target.value)}
              className="flex-1"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  addHeader();
                }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={addHeader}
              disabled={!newHeaderKey || !newHeaderValue}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Payload Template */}
      <div className="space-y-4 p-4 border border-border rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-gc-med text-foreground">
              Payload Template
            </h3>
            <Tooltip content="JSON payload to send to the external service. Must include {{webhook_url}} variable.">
              <Info className="w-4 h-4 text-muted-foreground" />
            </Tooltip>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={insertWebhookUrlVariable}
            className="text-xs">
            Insert {"{{webhook_url}}"}
          </Button>
        </div>

        <div className="space-y-2">
          <Textarea
            id="payload-template"
            value={formData.payload_template}
            onChange={(e) => handlePayloadChange(e.target.value)}
            placeholder='{\n  "callback_url": "{{webhook_url}}",\n  "event": "payment.created"\n}'
            rows={10}
            className={`font-mono text-sm ${
              payloadError ? "border-destructive" : ""
            }`}
          />
          {payloadError && (
            <p className="text-xs text-destructive">{payloadError}</p>
          )}
          {!hasWebhookUrl && !payloadError && (
            <p className="text-xs text-yellow-600">
              ⚠️ Payload must include {"{{webhook_url}}"} variable
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Use {"{{variable_name}}"} syntax to include variables from LLM
            parameters. The {"{{webhook_url}}"} variable is automatically
            provided.
          </p>
        </div>

        <div className="p-3 bg-card border border-border rounded-md">
          <p className="text-xs font-gc-med text-foreground mb-1">Example:</p>
          <pre className="text-xs text-muted-foreground overflow-x-auto">
            {`{
  "callback_url": "{{webhook_url}}",
  "amount": "{{amount}}",
  "customer_id": "{{customer_id}}"
}`}
          </pre>
        </div>
      </div>

      {/* Authentication */}
      <div className="space-y-4 p-4 border border-border rounded-lg">
        <div className="flex items-center gap-2">
          <KeyIcon className="w-5 h-5 text-maitai-green" />
          <h3 className="text-sm font-gc-med text-foreground">
            Authentication
          </h3>
          <Tooltip content="Configure authentication for the external service request">
            <Info className="w-4 h-4 text-muted-foreground" />
          </Tooltip>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="auth-type"
              className="text-sm font-gc-med text-foreground">
              Authentication Type
            </Label>
            <Select
              value={formData.auth?.type || "none"}
              onValueChange={(value) => handleAuthChange("type", value)}>
              <SelectTrigger id="auth-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="bearer">Bearer Token</SelectItem>
                <SelectItem value="api_key">API Key</SelectItem>
                <SelectItem value="basic">Basic Auth</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.auth?.type === "bearer" && (
            <div className="space-y-2">
              <Label
                htmlFor="bearer-token"
                className="text-sm font-gc-med text-foreground">
                Bearer Token
              </Label>
              <Input
                id="bearer-token"
                type="password"
                value={formData.auth?.token || ""}
                onChange={(e) => handleAuthChange("token", e.target.value)}
                placeholder="Enter bearer token"
                className="font-mono text-sm"
              />
            </div>
          )}

          {formData.auth?.type === "api_key" && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label
                  htmlFor="api-key-header"
                  className="text-sm font-gc-med text-foreground">
                  Header Name
                </Label>
                <Input
                  id="api-key-header"
                  value={formData.auth?.header || ""}
                  onChange={(e) => handleAuthChange("header", e.target.value)}
                  placeholder="X-API-Key"
                  className="font-mono text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="api-key-value"
                  className="text-sm font-gc-med text-foreground">
                  API Key
                </Label>
                <Input
                  id="api-key-value"
                  type="password"
                  value={formData.auth?.key || ""}
                  onChange={(e) => handleAuthChange("key", e.target.value)}
                  placeholder="Enter API key"
                  className="font-mono text-sm"
                />
              </div>
            </div>
          )}

          {formData.auth?.type === "basic" && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label
                  htmlFor="basic-username"
                  className="text-sm font-gc-med text-foreground">
                  Username
                </Label>
                <Input
                  id="basic-username"
                  value={formData.auth?.username || ""}
                  onChange={(e) => handleAuthChange("username", e.target.value)}
                  placeholder="Enter username"
                  className="font-mono text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="basic-password"
                  className="text-sm font-gc-med text-foreground">
                  Password
                </Label>
                <Input
                  id="basic-password"
                  type="password"
                  value={formData.auth?.password || ""}
                  onChange={(e) => handleAuthChange("password", e.target.value)}
                  placeholder="Enter password"
                  className="font-mono text-sm"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Timeout */}
      <div className="space-y-4 p-4 border border-border rounded-lg">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-gc-med text-foreground">Timeout</h3>
          <Tooltip content="How long the agent will wait for the webhook callback (in milliseconds)">
            <Info className="w-4 h-4 text-muted-foreground" />
          </Tooltip>
        </div>

        <div className="space-y-2">
          <Input
            type="number"
            value={formData.timeout_ms}
            onChange={(e) =>
              updateConfig({ timeout_ms: parseInt(e.target.value) || 600000 })
            }
            min={1000}
            max={3600000}
          />
          <p className="text-xs text-muted-foreground">
            Timeout in milliseconds (1000ms = 1 second). Default: 600000ms (10
            minutes)
          </p>
        </div>
      </div>

      {/* How It Works */}
      <div className="p-4 bg-muted border border-border rounded-lg">
        <h3 className="text-sm font-gc-med text-foreground mb-2">
          How Webhook Creation Works
        </h3>
        <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
          <li>Agent creates a unique webhook URL</li>
          <li>
            Agent sends configured request to external service with webhook URL
            in payload
          </li>
          <li>Agent pauses execution (WAITING_WEBHOOK status)</li>
          <li>
            External service processes request and calls webhook URL when ready
          </li>
          <li>Agent receives callback and continues execution</li>
        </ol>
      </div>
    </div>
  );
};

export default AgentActionWebhookEdit;
